/**
 * DiscussionGuideEditor — authoring UI for the experimental conversational survey style.
 *
 * The creator writes a freeform markdown discussion guide (the moderator decomposes it itself,
 * exactly like a real qual guide) plus a short list of mandatory questions that must be asked
 * verbatim, and an optional turn cap. Controlled component: owns no persistence.
 */

import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { DiscussionGuide, MandatoryQuestion } from '../../utils/surveyApi';

export const DEFAULT_GUIDE_TEMPLATE = `### INTRODUCTION
Goal: set a relaxed tone; make clear there are no right answers.
Points to hit: warm welcome; frame the topics; stress it's a real conversation, not a quiz.

### SECTION 1 — <TOPIC>
Goal: <what this section is trying to learn — the decision it informs>.
Points to hit: <the specific things you need captured before moving on>.
When to move on: <the exit condition>.

### SECTION 2 — <TOPIC>
Goal: <...>.
Points to hit: <...>.
When to move on: <...>.
`;

interface DiscussionGuideEditorProps {
  value: DiscussionGuide;
  onChange: (next: DiscussionGuide) => void;
  readOnly?: boolean;
}

const newMandatoryId = (existing: MandatoryQuestion[]): string => {
  let n = existing.length + 1;
  const ids = new Set(existing.map((m) => m.id));
  while (ids.has(`m${n}`)) n += 1;
  return `m${n}`;
};

const DiscussionGuideEditor: React.FC<DiscussionGuideEditorProps> = ({
  value,
  onChange,
  readOnly = false,
}) => {
  const guideMarkdown = value.guide_markdown ?? '';
  const mandatory = value.mandatory_questions ?? [];
  const maxTurns = value.max_turns;

  const update = (patch: Partial<DiscussionGuide>) => onChange({ ...value, ...patch });

  const setMandatoryText = (id: string, text: string) =>
    update({ mandatory_questions: mandatory.map((m) => (m.id === id ? { ...m, text } : m)) });

  const addMandatory = () =>
    update({ mandatory_questions: [...mandatory, { id: newMandatoryId(mandatory), text: '' }] });

  const removeMandatory = (id: string) =>
    update({ mandatory_questions: mandatory.filter((m) => m.id !== id) });

  if (readOnly) {
    return (
      <div className="space-y-3 text-sm">
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Discussion guide
          </div>
          <pre className="whitespace-pre-wrap font-sans text-gray-800 bg-gray-50 border border-gray-200 rounded-md p-3">
            {guideMarkdown || '(no guide)'}
          </pre>
        </div>
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Mandatory questions
          </div>
          {mandatory.length === 0 ? (
            <p className="text-gray-500">None</p>
          ) : (
            <ul className="list-disc pl-5 text-gray-800">
              {mandatory.map((m) => (
                <li key={m.id}>{m.text}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Guide markdown */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">Discussion guide</label>
        <p className="text-xs text-gray-500 mb-2">
          Write your topics as a real qual guide — sections with a Goal, “Points to hit”, and a
          “When to move on” cue. The AI moderator decomposes this itself, asks one question at a
          time, probes the gaps, and skips anything the respondent already covered. It's a map, not
          a script.
        </p>
        <textarea
          value={guideMarkdown}
          onChange={(e) => update({ guide_markdown: e.target.value })}
          rows={14}
          placeholder={DEFAULT_GUIDE_TEMPLATE}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#07E5D1] focus:border-transparent"
        />
        {!guideMarkdown && (
          <button
            type="button"
            onClick={() => update({ guide_markdown: DEFAULT_GUIDE_TEMPLATE })}
            className="mt-2 text-xs text-[#07E5D1] hover:underline"
          >
            Insert a starter template
          </button>
        )}
      </div>

      {/* Mandatory questions */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">Mandatory questions</label>
        <p className="text-xs text-gray-500 mb-2">
          These are asked <span className="font-medium">verbatim</span> before the interview can end
          — use them for anything you must capture exactly (e.g. a 1–10 rating).
        </p>
        <div className="space-y-2">
          {mandatory.map((m) => (
            <div key={m.id} className="flex items-center gap-2">
              <input
                type="text"
                value={m.text}
                onChange={(e) => setMandatoryText(m.id, e.target.value)}
                placeholder="e.g. On a scale of 1 to 10, how likely are you to buy this again?"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#07E5D1] focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => removeMandatory(m.id)}
                className="p-2 text-gray-400 hover:text-[#EF4444] transition-colors"
                title="Remove"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addMandatory}
          className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#374151] border border-gray-300 rounded-md px-3 py-1.5 hover:bg-gray-50 transition-colors"
        >
          <Plus className="h-4 w-4" /> Add mandatory question
        </button>
      </div>

      {/* Max turns */}
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">
          Max questions (safety cap)
        </label>
        <input
          type="number"
          min={1}
          value={maxTurns ?? ''}
          onChange={(e) =>
            update({ max_turns: e.target.value ? parseInt(e.target.value, 10) : undefined })
          }
          placeholder="25"
          className="w-32 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#07E5D1] focus:border-transparent"
        />
        <p className="text-xs text-gray-500 mt-1">
          The conversation always ends by this many questions. Default 25.
        </p>
      </div>
    </div>
  );
};

export default DiscussionGuideEditor;
